import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AppIcons } from '@/components/AppIcons'
import { CardItem } from '@/components/CardItem'
import { SavingHistoryItem } from '@/components/SavingHistoryItem'
import { BaseText, BaseView } from '@/components/base'
import { SkeletonCard } from '@/components/SkeletonCard'
import { AdaptedGoalTransferDTO } from '@/domain/transfer'
import { responsiveAdapter } from '@/utils'

const ITEM_HEIGHT = 72
const MAX_SIZE_LIST = 3
const MAX_LIST_HEIGHT = ITEM_HEIGHT * MAX_SIZE_LIST

export interface SavingsLastActivitiesListProps {
  goalId: string
  skeletonActive?: boolean
  transfers: AdaptedGoalTransferDTO[]
}

export const SavingsLastActivitiesList: React.FC<
  SavingsLastActivitiesListProps
> = ({ goalId, skeletonActive, transfers }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const redirectToUserTransactions = (goalId: string) => {
    navigate(`/savings/transactions-list/${goalId}`)
  }

  if (skeletonActive) {
    return <SkeletonCard height={228.33} radius={24} />
  }

  return (
    <CardItem.Root
      style={{
        paddingTop: `${responsiveAdapter(20)}px`,
        paddingBottom: `${responsiveAdapter(20)}px`,
        paddingLeft: 0,
        paddingRight: 0,
      }}
      onPress={
        transfers.length ? () => redirectToUserTransactions(goalId) : undefined
      }
    >
      <BaseView fullWidth gap={12}>
        <BaseText
          fontSize={20}
          fontFamily="semibold"
          style={{
            paddingLeft: `${responsiveAdapter(12)}px`,
            paddingRight: `${responsiveAdapter(12)}px`,
          }}
        >
          {t('activity')}
        </BaseText>

        {/* Empty state */}
        {transfers.length === 0 && (
          <BaseView row gap={8} align="center" px={12}>
            <AppIcons name="clock" color="icon.disabledInputIcon" size={20} />
            <BaseText color="label.description">
              {t('emptyTransactions')}
            </BaseText>
          </BaseView>
        )}

        {/* Transactions list */}
        {transfers.length > 0 && (
          <BaseView
            style={{
              ...(transfers.length > 2 && {
                maxHeight: `${MAX_LIST_HEIGHT}px`,
                overflowY: 'hidden',
              }),
            }}
          >
            {transfers.slice(0, 3).map((transfer) => (
              <SavingHistoryItem
                key={transfer.id}
                height={ITEM_HEIGHT}
                isLastActivities={true}
                {...transfer}
              />
            ))}
          </BaseView>
        )}
      </BaseView>
    </CardItem.Root>
  )
}
